/**
 * Authenticated API helper
 * ────────────────────────────────────────────────────────────────
 * Axios instance that sends both the cookie AND a Bearer token
 * from localStorage. This ensures authenticated API calls work
 * even when the browser blocks third-party cookies (Safari, Brave,
 * strict Chrome/Firefox) — which happens because the frontend
 * (soulsathiya.com / Vercel) and backend (onrender.com) are on
 * different domains.
 *
 * Usage:
 *   import api from '@/utils/api';
 *   const res = await api.get('/api/auth/me');
 */
import axios from 'axios';
import { BACKEND_URL } from './backendUrl';

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

// Attach Bearer token on every request (fallback for blocked cookies).
// A super-admin diagnostic view-as session (per-tab, sessionStorage) sends its
// own header instead — the backend gives it precedence over cookie/Bearer.
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const diagToken = sessionStorage.getItem('ss_diag_token');
  if (diagToken) {
    config.headers['X-Diagnostic-Token'] = diagToken;
  }
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;

/** Diagnostic view-as helpers (super-admin, read-only, per-tab). */
export function getDiagnosticToken() {
  return sessionStorage.getItem('ss_diag_token');
}

export function clearDiagnosticToken() {
  sessionStorage.removeItem('ss_diag_token');
}

/**
 * Store session token from login/register response.
 * Call this after any successful authentication response.
 */
export function saveSessionToken(responseData) {
  if (responseData?.token) {
    localStorage.setItem('session_token', responseData.token);
  }
}

/**
 * Clear session token on logout.
 * Also clears journey type so a different user logging in on the same device
 * doesn't inherit the previous user's routing.
 */
export function clearSessionToken() {
  localStorage.removeItem('session_token');
  localStorage.removeItem('ss_journey_type');
  // Per-account state that must not survive into the next sign-in on this
  // device: an unfinished onboarding draft (a new member once opened onboarding
  // to find the previous account's gender, age and marital status prefilled)
  // and the dashboard's instant-paint stats cache.
  localStorage.removeItem('soulsathiya_onboarding_state');
  localStorage.removeItem('ss_dash_stats');
}

/**
 * Human-readable message for a FastAPI/Pydantic validation error response.
 * Pydantic v2's default missing-field message is the unhelpful "Field required" —
 * this pairs it with the actual field name (e.g. "Occupation is required")
 * instead of leaving the user guessing which field is missing.
 */
export function getValidationErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null;
        const label = typeof field === 'string'
          ? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : null;
        if (!label) return d?.msg;
        if (/^field required$/i.test(d?.msg || '')) return `${label} is required`;
        return `${label}: ${d?.msg}`;
      })
      .filter(Boolean);
    return messages.length ? messages.join(' · ') : fallback;
  }

  if (typeof detail === 'string' && detail) return detail;
  return error?.message || fallback;
}

/**
 * User-facing message for failed POST /api/interests/send.
 */
export function interestSendErrorMessage(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const msg = Array.isArray(detail) ? detail[0]?.msg : detail;

  if (detail === 'photo_required' || msg === 'photo_required') {
    return 'Please upload at least one profile photo before sending interests.';
  }
  if (detail === 'email_not_verified' || msg === 'email_not_verified') {
    return 'Please verify your email to send interests — a 6-digit code does it in under a minute.';
  }
  if (status === 429 && typeof msg === 'string') return msg;
  if (status === 403 && typeof msg === 'string') return msg;
  if (msg === 'Interest already sent') {
    return 'You already sent a connection request to this person.';
  }
  if (msg === 'Cannot send interest to yourself') {
    return 'You cannot connect with your own profile.';
  }
  if (typeof msg === 'string' && msg.length > 0) return msg;
  return 'Failed to send connection request.';
}
